import { FC, useCallback } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import NewAnalyticsApi from 'api/new-analytics';
import type { SyncBatch } from 'api/new-analytics/types';
import { queryKey } from 'constants/queryKey';
import routes from 'constants/routes';
import Logger from 'utils/logger';
import { useAnalyticsSyncBatchState } from 'app/modules/analytics/hooks/useAnalyticsSync';
import { useAnalyticsSyncStore } from 'app/modules/analytics/components/AnalyticsSyncDriver';
import { notifyAnalyticsSyncFinished } from 'app/modules/analytics/utils/analyticsSyncFinishedToast';
import { invalidateAnalyticsSyncRunQueries } from 'app/modules/analytics/utils/invalidateAnalyticsSyncRunQueries';

const POLL_INTERVAL_MS = 3000;

function areAllBatchesTerminal(batches: SyncBatch[]): boolean {
  return batches.every(
    (batch) => batch.status === 'completed' || batch.status === 'failed',
  );
}

/**
 * Headless, mounted once in the authenticated app shell (above the router
 * outlet). Keeps polling an in-flight analytics sync's batch status when the
 * analytics `AnalyticsSyncDriver` is not mounted (i.e. the user navigated away
 * from Analytics), so the user is still notified when the sync finishes.
 */
const AnalyticsSyncCompletionWatcher: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const isSyncDriverMounted = useAnalyticsSyncStore(
    (state) => state.isSyncDriverMounted,
  );
  const batchIds = useAnalyticsSyncBatchState((state) => state.batchIds);
  const hasExceededMaxDuration = useAnalyticsSyncBatchState(
    (state) => state.hasExceededMaxDuration,
  );
  const reset = useAnalyticsSyncBatchState((state) => state.reset);

  const isEnabled = !isSyncDriverMounted && batchIds.length > 0;

  const handleTerminal = useCallback(
    (batches: SyncBatch[]) => {
      const store = useAnalyticsSyncBatchState.getState();
      if (!store.tryConsumeCompletionOnce()) {
        reset();
        return;
      }
      const hasFailure = batches.some((batch) => batch.status === 'failed');
      const isFullRunComplete = store.pendingChunkCount === 0;
      if (!hasFailure && !isFullRunComplete) {
        Logger.info(
          '[AnalyticsSyncWatcher] Chunk finished off-page; run not complete',
          { pendingChunkCount: store.pendingChunkCount },
        );
        reset();
        return;
      }
      invalidateAnalyticsSyncRunQueries(queryClient, store);
      notifyAnalyticsSyncFinished({
        t,
        targetLabel: useAnalyticsSyncStore.getState().syncTargetLabel,
        hasFailure,
        onView: () => navigate(routes.ANALYTICS_OVERVIEW),
      });
      reset();
    },
    [navigate, queryClient, reset, t],
  );

  useQuery(
    queryKey.analyticsSyncBatch(batchIds),
    () =>
      Promise.all(
        batchIds.map((batchId) => NewAnalyticsApi.getSyncBatchStatus(batchId)),
      ),
    {
      enabled: isEnabled,
      refetchInterval: (data: SyncBatch[] | undefined) => {
        if (hasExceededMaxDuration()) return false;
        if (!data?.length) return POLL_INTERVAL_MS;
        return areAllBatchesTerminal(data) ? false : POLL_INTERVAL_MS;
      },
      onSuccess: (data: SyncBatch[]) => {
        if (hasExceededMaxDuration()) {
          reset();
          return;
        }
        if (!data.length || !areAllBatchesTerminal(data)) return;
        handleTerminal(data);
      },
      onError: (error: unknown) => {
        Logger.error(
          '[AnalyticsSyncWatcher] Status poll failed',
          error instanceof Error ? error.message : String(error),
        );
        reset();
      },
      staleTime: 0,
    },
  );

  return null;
};

export default AnalyticsSyncCompletionWatcher;
